/* House of Maahi — Shopflo-style cart drawer behaviour (31 Oct 2026).
 *
 *   · copy-on-tap coupon code in the offer ticker
 *   · Best Offers / People also added tabs and their arrows
 *   · + ADD on a suggestion
 *   · the product sheet: big swipeable photos, our description, "Proceed"
 *     (adds the saree and returns to the cart — merchant's choice)
 *   · confetti + "Added to your cart" toast whenever a saree is added
 *
 * Loaded once from layout/theme.liquid. Every listener is delegated from
 * document because the drawer is redrawn (morphed) on every cart change; the
 * sheet, confetti canvas and toast live in .maahi-cx-layer, which the morph is
 * told to leave alone (data-skip-subtree-update).
 *
 * RAZORPAY: nothing here touches the checkout button, the cart form or
 * checkout. Adding uses the same /cart/add.js + `cart:update` route as the
 * old suggestion rail (assets/maahi-cart-recs.js), so the theme redraws the
 * drawer itself and maahi-razorpay-cart-rehydrate.js keeps Razorpay's button
 * alive exactly as before. Every handler is wrapped so a failure here can
 * only leave the server-rendered cart showing.
 */
(function () {
  'use strict';
  if (window.__maahiCxBound) return;
  window.__maahiCxBound = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ADD_SOURCES = { 'product-form-component': 1, 'maahi-cx': 1, 'maahi-cart-recs': 1 };
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var activeTab = 'best';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function dialog() { return $('.cart-drawer__dialog'); }
  function layer() { return $('[data-maahi-cx-layer]'); }

  function rupees(paise) {
    var r = Math.round(paise / 100);
    try { return '₹' + r.toLocaleString('en-IN'); } catch (e) { return '₹' + r; }
  }

  function sized(url, w) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* ── adding to the cart ─────────────────────────────────────────────── */
  function addVariant(id) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (j) {
            var err = new Error((j && (j.description || j.message)) || 'Could not add this saree.');
            err.userFacing = true;
            throw err;
          });
        }
        return fetch(ROOT + 'cart.js').then(function (r2) { return r2.json(); });
      })
      .then(function (cart) {
        /* No `sections`: the cart component redraws itself, and the drawer
           stays open (it is already open, and auto-open keeps it so). */
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'maahi-cx', data: { source: 'maahi-cx', itemCount: 1 } }
        }));
        return cart;
      });
  }

  /* ── tabs & arrows ──────────────────────────────────────────────────── */
  function applyTab(name) {
    var box = $('[data-maahi-cx-recs]');
    if (!box) return;
    if (!box.querySelector('[data-cx-panel="' + name + '"] .maahi-cx-card')) name = 'best';
    box.querySelectorAll('[data-cx-tab]').forEach(function (t) {
      t.setAttribute('aria-selected', t.getAttribute('data-cx-tab') === name ? 'true' : 'false');
    });
    box.querySelectorAll('[data-cx-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-cx-panel') !== name;
      syncArrows(p);
    });
  }

  function syncArrows(panel) {
    if (!panel || panel.hidden) return;
    var track = panel.querySelector('[data-cx-track]');
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    var prev = panel.querySelector('[data-cx-prev]');
    var next = panel.querySelector('[data-cx-next]');
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
  }

  /* ── favourites ─────────────────────────────────────────────────────
     The cart's hearts write the same localStorage list, in the same shape,
     as assets/favourites.js, and fire its favourites:updated event, so the
     header badge, the favourites drawer and every other heart follow. One
     delegated listener — a tap can never toggle twice. */
  var FAV_KEY = 'maahi_favourites';
  function readFavs() {
    try { var v = JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; }
  }
  function toggleHeart(el) {
    var d = el.dataset;
    var favs = readFavs();
    var i = favs.findIndex(function (f) { return String(f.id) === String(d.productId); });
    if (i === -1) {
      favs.push({
        id: d.productId,
        title: d.productTitle,
        handle: d.productHandle,
        price: d.productPrice,
        compareAtPrice: d.productCompareAtPrice || '',
        variantId: d.productVariantId || '',
        silk: d.productSilk === 'true',
        image: d.productImage || '',
        url: d.productUrl
      });
    } else {
      favs.splice(i, 1);
    }
    try { localStorage.setItem(FAV_KEY, JSON.stringify(favs)); } catch (e) { return; }
    window.dispatchEvent(new CustomEvent('favourites:updated', { detail: { favourites: favs } }));
    syncHearts();
  }

  /* a redraw resets the hearts to the server markup: put the saved state back */
  function syncHearts() {
    var favs = readFavs();
    document.querySelectorAll('.cart-drawer__dialog .maahi-cx-fav').forEach(function (el) {
      var on = favs.some(function (f) { return String(f.id) === String(el.getAttribute('data-product-id')); });
      el.classList.toggle('is-favourite', on);
      var b = el.querySelector('button');
      if (b) {
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', on ? 'Remove from favourites' : 'Add to favourites');
      }
    });
  }

  function afterRedraw() {
    applyTab(activeTab);
    syncHearts();
  }

  /* ── toast ──────────────────────────────────────────────────────────── */
  var toastTimer;
  function toast(text) {
    var l = layer();
    if (!l) return;
    var t = l.querySelector('.maahi-cx-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'maahi-cx-toast';
      t.setAttribute('role', 'status');
      t.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="m8 12.3 2.7 2.7L16.2 9.4"/></svg><span></span>';
      l.appendChild(t);
    }
    t.querySelector('span').textContent = text;
    /* float just above the footer, never over the strip or the total */
    var foot = document.querySelector('.cart-drawer__dialog .cart-drawer__summary');
    if (foot) {
      var fr = foot.getBoundingClientRect(), lr = l.getBoundingClientRect();
      if (fr.height) t.style.bottom = Math.max(12, Math.round(lr.bottom - fr.top + 10)) + 'px';
    }
    clearTimeout(toastTimer);
    requestAnimationFrame(function () { t.classList.add('is-on'); });
    toastTimer = setTimeout(function () { t.classList.remove('is-on'); }, 2200);
  }

  /* ── confetti ───────────────────────────────────────────────────────── */
  var COLORS = ['#3e1414', '#7a1f2b', '#c2a05a', '#f1d48d', '#e58aa0', '#b5476a', '#fff4dc', '#1f6f6b'];
  var confettiRun = 0;
  function confetti() {
    if (reduceMotion) return;
    var l = layer();
    if (!l) return;
    var canvas = l.querySelector('.maahi-cx-confetti');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'maahi-cx-confetti';
      canvas.setAttribute('aria-hidden', 'true');
      l.insertBefore(canvas, l.firstChild);
    }
    var w = l.clientWidth, h = l.clientHeight;
    if (!w || !h) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    /* After the reference: a burst across the top that spreads outwards and
       drifts down, every piece fading out on its own as it falls, so the
       shower thins away smoothly instead of disappearing at once. Positions
       come from elapsed time (not per-frame steps), so a busy first second
       while the drawer opens cannot freeze it. */
    var n = Math.round(Math.min(170, w * 0.44));
    var parts = [];
    for (var i = 0; i < n; i++) {
      var x0 = Math.random() * w;
      parts.push({
        x0: x0,
        y0: -16 + Math.random() * 46,
        vx: (x0 - w / 2) / (w / 2) * (40 + Math.random() * 90) + (Math.random() - 0.5) * 60,   /* fan out, px/s */
        vy: -60 + Math.random() * 190,                                                           /* some kick up first */
        amp: 4 + Math.random() * 10,
        ph: Math.random() * Math.PI * 2,
        fr: 3 + Math.random() * 4,
        r0: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 8,
        life: 1500 + Math.random() * 1100,          /* each piece's own fade-out, ms */
        w: 5 + Math.random() * 5,
        h: 3 + Math.random() * 3.5,
        c: COLORS[(Math.random() * COLORS.length) | 0]
      });
    }
    /* Fade out above the footer: every piece is gone before it reaches the
       "Coupons & Discounts…" strip and the Estimated total. */
    var limit = h;
    var foot = document.querySelector('.cart-drawer__dialog .cart-drawer__summary');
    if (foot) {
      var fr = foot.getBoundingClientRect(), lr = l.getBoundingClientRect();
      if (fr.height) limit = Math.max(120, Math.min(h, fr.top - lr.top - 4));
    }
    var FADE_ZONE = 110;
    var run = ++confettiRun;
    var G = 230;      /* px/s² */
    var DRAG = 0.55;  /* 1/s, horizontal air drag */
    var DUR = 2700;
    var start = 0;
    function frame(now) {
      if (run !== confettiRun) return;
      if (!start) start = now;
      var t = now - start, s = t / 1000;
      ctx.clearRect(0, 0, w, h);
      var drift = (1 - Math.exp(-DRAG * s)) / DRAG;   /* ∫ e^(-k s) ds */
      for (var k = 0; k < parts.length; k++) {
        var p = parts[k];
        var a = 1 - Math.pow(Math.min(1, t / p.life), 1.6);   /* smooth individual fade */
        if (a <= 0.01) continue;
        var y = p.y0 + p.vy * s + 0.5 * G * s * s;
        if (y >= limit) continue;
        a *= Math.min(1, (limit - y) / FADE_ZONE);
        if (a <= 0.01) continue;
        var x = p.x0 + p.vx * drift + Math.sin(p.ph + p.fr * s) * p.amp;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(x, y);
        ctx.rotate(p.r0 + p.vr * s);
        ctx.scale(1, Math.cos(p.ph + p.fr * 1.3 * s));   /* the paper flips as it falls */
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (t < DUR) requestAnimationFrame(frame);
      else ctx.clearRect(0, 0, w, h);
    }
    requestAnimationFrame(frame);
  }

  /* fire once the drawer is actually open (it opens on the same event) */
  function celebrate() {
    var tries = 0;
    (function wait() {
      var d = dialog();
      if (d && d.open) {
        /* after the drawer's slide-in, so the paper falls over a settled cart */
        setTimeout(function () { confetti(); toast('Added to your cart'); }, 380);
      } else if (tries++ < 100) {   /* up to 10 s: slow networks open the drawer late */
        setTimeout(wait, 100);
      }
    })();
  }

  /* ── product sheet ──────────────────────────────────────────────────── */
  var sheet = null;
  var lastFocus = null;

  var ALLOWED = { P: 1, H3: 1, H4: 1, UL: 1, OL: 1, LI: 1, STRONG: 1, B: 1, EM: 1, I: 1, BR: 1 };
  var DROP = /^(SCRIPT|STYLE|IFRAME|OBJECT|EMBED|SVG|IMG|VIDEO|AUDIO|FORM|INPUT|BUTTON|TEMPLATE|LINK|META)$/;
  function cleanInto(html, out) {
    var doc = new DOMParser().parseFromString(html || '', 'text/html');
    (function walk(node, into) {
      node.childNodes.forEach(function (ch) {
        if (ch.nodeType === 3) {
          into.appendChild(document.createTextNode(ch.nodeValue));
        } else if (ch.nodeType === 1) {
          if (DROP.test(ch.tagName)) return;
          if (ALLOWED[ch.tagName]) {
            var el = document.createElement(ch.tagName.toLowerCase());
            walk(ch, el);
            into.appendChild(el);
          } else {
            walk(ch, into);
          }
        }
      });
    })(doc.body, out);
  }

  function closeSheet() {
    if (!sheet) return;
    var s = sheet;
    sheet = null;
    s.classList.remove('is-open');
    setTimeout(function () { if (s.parentNode) s.parentNode.removeChild(s); }, 450);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }

  function openSheet(handle, opener) {
    var l = layer();
    if (!l || !handle) return false;
    if (sheet) closeSheet();
    lastFocus = opener || null;

    var s = document.createElement('div');
    s.className = 'maahi-cx-sheet';
    s.innerHTML =
      '<div class="maahi-cx-sheet__scrim" data-cx-close></div>' +
      '<div class="maahi-cx-sheet__panel" role="dialog" aria-modal="true" aria-label="Saree details">' +
        '<div class="maahi-cx-sheet__media">' +
          '<div class="maahi-cx-sheet__bg"></div>' +
          '<div class="maahi-cx-sheet__track"><div class="maahi-cx-sheet__slide"><span class="maahi-cx-skel maahi-cx-skel--photo"></span></div></div>' +
          '<button type="button" class="maahi-cx-sheet__close" data-cx-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
          '<div class="maahi-cx-sheet__dots"></div>' +
        '</div>' +
        /* skeleton while the saree loads: title, price, offer chip, text lines */
        '<div class="maahi-cx-sheet__body" aria-busy="true">' +
          '<div class="maahi-cx-sheet__row">' +
            '<div class="maahi-cx-skel-stack"><span class="maahi-cx-skel" style="width:78%;height:18px"></span><span class="maahi-cx-skel" style="width:54%;height:18px"></span></div>' +
            '<div class="maahi-cx-skel-stack maahi-cx-skel-stack--end"><span class="maahi-cx-skel" style="width:62px;height:22px;border-radius:6px"></span><span class="maahi-cx-skel" style="width:84px;height:16px"></span></div>' +
          '</div>' +
          '<div class="maahi-cx-skel-stack maahi-cx-skel-stack--text"><span class="maahi-cx-skel"></span><span class="maahi-cx-skel"></span><span class="maahi-cx-skel"></span><span class="maahi-cx-skel" style="width:62%"></span></div>' +
        '</div>' +
        '<div class="maahi-cx-sheet__foot"><button type="button" class="maahi-cx-sheet__proceed" disabled>Proceed</button></div>' +
      '</div>';
    l.appendChild(s);
    sheet = s;
    requestAnimationFrame(function () { requestAnimationFrame(function () { s.classList.add('is-open'); }); });

    fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
      .then(function (p) { if (sheet === s) fillSheet(s, p); })
      .catch(function (err) {
        console.error('[maahi-cx]', err);
        /* could not load: fall back to the product page */
        if (sheet === s) { closeSheet(); window.location.href = ROOT + 'products/' + handle; }
      });
    return true;
  }

  function fillSheet(s, p) {
    var images = (p.images || []).slice(0, 12);
    var track = s.querySelector('.maahi-cx-sheet__track');
    var dots = s.querySelector('.maahi-cx-sheet__dots');
    var bg = s.querySelector('.maahi-cx-sheet__bg');
    track.innerHTML = '';
    dots.innerHTML = '';
    images.forEach(function (src, i) {
      var slide = document.createElement('div');
      slide.className = 'maahi-cx-sheet__slide';
      var img = document.createElement('img');
      img.src = sized(src, 720);
      img.srcset = sized(src, 480) + ' 480w, ' + sized(src, 720) + ' 720w, ' + sized(src, 960) + ' 960w';
      img.sizes = '300px';
      img.alt = p.title + (images.length > 1 ? ' — photo ' + (i + 1) + ' of ' + images.length : '');
      img.loading = i < 2 ? 'eager' : 'lazy';
      img.decoding = 'async';
      slide.appendChild(img);
      track.appendChild(slide);
      if (images.length > 1) dots.appendChild(document.createElement('i'));
    });
    var current = -1;
    function syncSlide() {
      var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i === current) return;
      current = i;
      dots.querySelectorAll('i').forEach(function (d, k) { d.classList.toggle('is-on', k === i); });
      if (images[i]) bg.style.backgroundImage = 'url("' + sized(images[i], 160) + '")';
    }
    track.addEventListener('scroll', syncSlide, { passive: true });
    syncSlide();

    var v = (p.variants || []).filter(function (x) { return x.available; })[0] || (p.variants || [])[0];
    var price = v ? v.price : p.price;
    var cmp = v && v.compare_at_price ? v.compare_at_price : p.compare_at_price;
    var pct = cmp && cmp > price ? Math.floor((cmp - price) * 100 / cmp) : 0;

    var body = s.querySelector('.maahi-cx-sheet__body');
    body.removeAttribute('aria-busy');
    body.innerHTML =
      '<div class="maahi-cx-sheet__row">' +
        '<h3 class="maahi-cx-sheet__title"></h3>' +
        '<div class="maahi-cx-sheet__pricebox">' +
          (pct > 0 ? '<span class="maahi-cx-sheet__off">' + pct + '% off</span>' : '') +
          '<p class="maahi-cx-sheet__price">' + (pct > 0 ? '<s>' + rupees(cmp) + '</s>' : '') + '<b>' + rupees(price) + '</b></p>' +
        '</div>' +
      '</div>' +
      '<div class="maahi-cx-sheet__desc"></div>';
    body.querySelector('.maahi-cx-sheet__title').textContent = p.title;
    cleanInto(p.description, body.querySelector('.maahi-cx-sheet__desc'));
    s.querySelector('.maahi-cx-sheet__panel').setAttribute('aria-label', p.title);

    var btn = s.querySelector('.maahi-cx-sheet__proceed');
    if (v && v.available) {
      btn.disabled = false;
      btn.setAttribute('data-cx-proceed', v.id);
    } else {
      btn.textContent = 'Sold out';
      btn.classList.add('is-gone');
    }
    try { btn.focus({ preventScroll: true }); } catch (e) {}
  }


  /* ── remove confirmation (after the merchant's reference) ──────────────
     The bin — and "−" at quantity 1, which would also delete the line —
     asks first: "Do you want to remove 1 item?", the saree, "Yes, remove".
     Caught on window in the capture phase, ahead of the theme's own
     document-level `on:click` handler; "Yes, remove" then replays the click
     with `rmBypass` set, so the theme removes the line exactly as before. */
  var rmSheet = null;
  var rmBypass = false;

  function closeRemove() {
    if (!rmSheet) return;
    var s = rmSheet;
    rmSheet = null;
    s.classList.remove('is-open');
    setTimeout(function () { if (s.parentNode) s.parentNode.removeChild(s); }, 420);
  }

  function lineFrom(btn) {
    var row = btn.closest('tr[data-key]');
    if (!row) return null;
    var img = row.querySelector('.cart-items__media-image');
    var mrp = row.querySelector('.maahi-cx-line-mrp');
    var input = row.querySelector('.quantity-selector input');
    var variant = row.querySelector('.cart-items__variants');
    return {
      key: row.getAttribute('data-key'),
      isMinus: btn.classList.contains('quantity-minus'),
      img: img ? (img.currentSrc || img.src) : '',
      title: ((row.querySelector('.cart-items__title') || {}).textContent || '').trim(),
      variant: variant ? variant.textContent.replace(/\s+/g, ' ').trim() : '',
      price: ((row.querySelector('.cart-items__price text-component') || {}).textContent || '').trim(),
      mrp: mrp ? mrp.textContent.replace(/regular price/i, '').replace(/\s+/g, ' ').trim() : '',
      qty: input ? (parseInt(input.value, 10) || 1) : 1
    };
  }

  function openRemove(btn) {
    var l = layer();
    var line = lineFrom(btn);
    if (!l || !line) return false;
    if (rmSheet) closeRemove();
    var n = line.isMinus ? 1 : line.qty;
    var s = document.createElement('div');
    s.className = 'maahi-cx-rm';
    s.innerHTML =
      '<div class="maahi-cx-rm__scrim" data-cx-rm-close></div>' +
      '<div class="maahi-cx-rm__panel" role="dialog" aria-modal="true" aria-labelledby="maahi-cx-rm-title">' +
        '<div class="maahi-cx-rm__head">' +
          '<h3 class="maahi-cx-rm__title" id="maahi-cx-rm-title"></h3>' +
          '<button type="button" class="maahi-cx-rm__close" data-cx-rm-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg></button>' +
        '</div>' +
        '<div class="maahi-cx-rm__item">' +
          '<span class="maahi-cx-rm__media"><img alt=""></span>' +
          '<div class="maahi-cx-rm__info">' +
            '<p class="maahi-cx-rm__name"></p>' +
            '<p class="maahi-cx-rm__variant"></p>' +
            '<span class="maahi-cx-rm__stepper" aria-hidden="true"><i>−</i><b></b><i>+</i></span>' +
          '</div>' +
          '<div class="maahi-cx-rm__price"><b></b><s></s></div>' +
        '</div>' +
        '<button type="button" class="maahi-cx-rm__yes" data-cx-rm-yes>Yes, remove</button>' +
      '</div>';
    s.querySelector('.maahi-cx-rm__title').textContent = 'Do you want to remove ' + n + (n === 1 ? ' item?' : ' items?');
    var img = s.querySelector('.maahi-cx-rm__media img');
    if (line.img) img.src = line.img; else img.parentNode.classList.add('is-empty');
    s.querySelector('.maahi-cx-rm__name').textContent = line.title;
    var v = s.querySelector('.maahi-cx-rm__variant');
    if (line.variant) v.textContent = line.variant; else v.parentNode.removeChild(v);
    s.querySelector('.maahi-cx-rm__stepper b').textContent = String(line.qty);
    s.querySelector('.maahi-cx-rm__price b').textContent = line.price;
    var st = s.querySelector('.maahi-cx-rm__price s');
    if (line.mrp) st.textContent = line.mrp; else st.parentNode.removeChild(st);
    s._line = line;
    l.appendChild(s);
    rmSheet = s;
    requestAnimationFrame(function () { requestAnimationFrame(function () { s.classList.add('is-open'); }); });
    setTimeout(function () { var y = s.querySelector('[data-cx-rm-yes]'); if (y) try { y.focus({ preventScroll: true }); } catch (e) {} }, 60);
    return true;
  }

  function confirmRemove() {
    if (!rmSheet) return;
    var line = rmSheet._line;
    closeRemove();
    var row = document.querySelector('.cart-drawer__dialog tr[data-key="' + (window.CSS && CSS.escape ? CSS.escape(line.key) : line.key) + '"]');
    var target = row && row.querySelector(line.isMinus ? '.quantity-selector .quantity-minus' : '.cart-items__remove');
    if (!target) return;
    rmBypass = true;
    try { target.click(); } finally { rmBypass = false; }
  }

  window.addEventListener('click', function (ev) {
    if (rmBypass) return;
    var t = ev.target;
    if (!t || !t.closest) return;
    try {
      var btn = t.closest('.cart-drawer__dialog .cart-items__remove, .cart-drawer__dialog .quantity-selector .quantity-minus');
      if (!btn) return;
      if (btn.classList.contains('quantity-minus')) {
        var input = btn.closest('.quantity-selector').querySelector('input');
        if (!input || (parseInt(input.value, 10) || 0) > 1) return;   /* a normal step down */
      }
      if (openRemove(btn)) {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
      }
    } catch (e) {
      console.error('[maahi-cx]', e);   /* fall through: the theme removes as before */
    }
  }, true);

  /* ── clicks ─────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    try {
      var copy = t.closest('[data-cx-copy]');
      if (copy) {
        ev.preventDefault();
        var code = copy.getAttribute('data-cx-copy');
        var done = function () {
          var label = copy.querySelector('span');
          copy.classList.add('is-copied');
          if (label) label.textContent = 'Copied!';
          setTimeout(function () {
            copy.classList.remove('is-copied');
            if (label) label.textContent = code;
          }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, function () { fallbackCopy(code); done(); });
        } else { fallbackCopy(code); done(); }
        return;
      }

      if (t.closest('[data-cx-rm-close]')) { ev.preventDefault(); closeRemove(); return; }
      if (t.closest('[data-cx-rm-yes]')) { ev.preventDefault(); confirmRemove(); return; }

      var heart = t.closest('.maahi-cx-fav__btn');
      if (heart) {
        ev.preventDefault();
        var holder = heart.closest('.maahi-cx-fav');
        if (holder) toggleHeart(holder);
        return;
      }

      var bill = t.closest('[data-cx-bill-toggle]');
      if (bill) {
        ev.preventDefault();
        var est = bill.closest('[data-cx-est]');
        var panel = est && est.querySelector('[data-cx-bill]');
        if (panel) {
          var open = !est.classList.contains('is-open');
          est.classList.toggle('is-open', open);
          if (open) panel.removeAttribute('inert'); else panel.setAttribute('inert', '');
          bill.setAttribute('aria-expanded', String(open));
        }
        return;
      }

      var tab = t.closest('[data-cx-tab]');
      if (tab) {
        ev.preventDefault();
        activeTab = tab.getAttribute('data-cx-tab');
        applyTab(activeTab);
        return;
      }

      var arrow = t.closest('[data-cx-prev], [data-cx-next]');
      if (arrow) {
        ev.preventDefault();
        var track = arrow.parentNode.querySelector('[data-cx-track]');
        if (track) {
          /* one card per tap, from wherever the rail rests (a swipe can leave
             it one card in) — an exact target, never past either end */
          var card = track.querySelector('.maahi-cx-card');
          var step = card ? card.offsetWidth : track.clientWidth / 2;
          var max = Math.max(0, track.scrollWidth - track.clientWidth);
          var idx = Math.round(track.scrollLeft / step) + (arrow.hasAttribute('data-cx-prev') ? -1 : 1);
          var to = Math.min(max, Math.max(0, idx * step));
          if (max - to < step / 2) to = max;
          track.scrollTo({ left: to, behavior: 'smooth' });
        }
        return;
      }

      var add = t.closest('[data-cx-add]');
      if (add) {
        ev.preventDefault();
        if (add.classList.contains('is-adding')) return;
        add.classList.add('is-adding');
        addVariant(add.getAttribute('data-cx-add')).catch(function (err) {
          console.error('[maahi-cx]', err);
          add.classList.remove('is-adding');
          if (err.userFacing) toast(err.message);
        });
        return;
      }

      var open = t.closest('[data-cx-open]');
      if (open && !ev.metaKey && !ev.ctrlKey && !ev.shiftKey) {
        if (openSheet(open.getAttribute('data-cx-open'), open)) ev.preventDefault();
        return;
      }

      if (t.closest('[data-cx-close]')) {
        ev.preventDefault();
        closeSheet();
        return;
      }

      var proceed = t.closest('[data-cx-proceed]');
      if (proceed) {
        ev.preventDefault();
        proceed.disabled = true;
        proceed.textContent = 'Adding…';
        addVariant(proceed.getAttribute('data-cx-proceed'))
          .then(function () { closeSheet(); })
          .catch(function (err) {
            console.error('[maahi-cx]', err);
            proceed.disabled = false;
            proceed.textContent = 'Proceed';
            var foot = proceed.parentNode;
            var msg = foot.querySelector('.maahi-cx-sheet__err') || foot.appendChild(document.createElement('p'));
            msg.className = 'maahi-cx-sheet__err';
            msg.textContent = err.userFacing ? err.message : 'Could not add this saree. Please try again.';
          });
      }
    } catch (e) {
      console.error('[maahi-cx]', e);
    }
  });

  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
      (dialog() || document.body).appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.parentNode.removeChild(ta);
    } catch (e) {}
  }

  /* Esc closes the sheet first, not the whole cart. */
  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && rmSheet) {
      ev.preventDefault();
      ev.stopPropagation();
      closeRemove();
      return;
    }
    if (ev.key === 'Escape' && sheet) {
      ev.preventDefault();
      ev.stopPropagation();
      closeSheet();
    }
  }, true);
  document.addEventListener('cancel', function (ev) {
    if (ev.target !== dialog()) return;
    if (rmSheet) { ev.preventDefault(); closeRemove(); return; }
    if (sheet) { ev.preventDefault(); closeSheet(); }
  }, true);

  /* arrows follow the rail */
  document.addEventListener('scroll', function (ev) {
    var el = ev.target;
    if (el && el.matches && el.matches('[data-cx-track]')) syncArrows(el.closest('[data-cx-panel]'));
  }, true);

  /* ── cart changes ───────────────────────────────────────────────────── */
  document.addEventListener('cart:update', function (ev) {
    try {
      var d = (ev && ev.detail) || {};
      var data = d.data || {};
      if (!data.didError && ADD_SOURCES[data.source]) celebrate();
      if (sheet && data.source !== 'maahi-cx') closeSheet();
    } catch (e) {}
    setTimeout(afterRedraw, 250);
    setTimeout(afterRedraw, 900);
  });

  /* hearts changed anywhere on the site (product page, cards, drawer) */
  window.addEventListener('favourites:updated', function () { try { syncHearts(); } catch (e) {} });

  /* When the drawer closes, put any open sheet away. */
  document.addEventListener('close', function (ev) {
    if (ev.target !== dialog()) return;
    if (sheet) closeSheet();
    if (rmSheet) closeRemove();
  }, true);

  function init() {
    afterRedraw();
    /* the rails measure 0 wide while the drawer is closed: re-sync on open */
    var d = dialog();
    if (d && window.MutationObserver) {
      new MutationObserver(function () { if (d.open) { setTimeout(afterRedraw, 60); setTimeout(afterRedraw, 500); } })
        .observe(d, { attributes: true, attributeFilter: ['open'] });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
